<script setup>
import { computed } from 'vue';
import ChannelSelector from '../ChannelSelector.vue';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['channelItemClick']);

// Mapping of channel key to feature flag name
const CHANNEL_FEATURE_MAP = {
  website: 'channel_website',
  facebook: 'channel_facebook',
  whatsapp: 'channel_whatsapp',
  sms: 'channel_sms',
  email: 'channel_email',
  api: 'channel_api',
  telegram: 'channel_telegram',
  line: 'channel_line',
  instagram: 'channel_instagram',
  voice: 'channel_voice',
};

const hasFbConfigured = computed(() => {
  return window.chatwootConfig?.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  return window.chatwootConfig?.instagramAppId;
});

// Check if channel is unavailable due to plan restrictions
const isUnavailableForPlan = computed(() => {
  const { key } = props.channel;
  const featureKey = CHANNEL_FEATURE_MAP[key];

  // If no feature mapping exists, it's not restricted by plan
  if (!featureKey) return false;

  // If features are not loaded yet, don't show as unavailable
  if (Object.keys(props.enabledFeatures).length === 0) return false;

  // Check if the feature is explicitly set to false (restricted by plan)
  return props.enabledFeatures[featureKey] === false;
});

const isActive = computed(() => {
  const { key } = props.channel;
  if (Object.keys(props.enabledFeatures).length === 0) {
    return false;
  }

  // If unavailable for plan, it's not active
  if (isUnavailableForPlan.value) {
    return false;
  }

  // Check specific configurations for some channels
  if (key === 'facebook') {
    return props.enabledFeatures.channel_facebook && hasFbConfigured.value;
  }

  if (key === 'instagram') {
    return (
      props.enabledFeatures.channel_instagram && hasInstagramConfigured.value
    );
  }

  // For channels with feature flags, check if enabled
  const featureKey = CHANNEL_FEATURE_MAP[key];
  if (featureKey && props.enabledFeatures[featureKey] !== undefined) {
    return props.enabledFeatures[featureKey];
  }

  // Default: channel is active if it's in the known list
  return [
    'website',
    'twilio',
    'api',
    'whatsapp',
    'sms',
    'telegram',
    'line',
    'instagram',
    'voice',
  ].includes(key);
});

const isComingSoon = computed(() => {
  const { key } = props.channel;
  // Show "Coming Soon" only if the channel is marked as coming soon
  // and the corresponding feature flag is not enabled yet.
  // Don't show coming soon if it's unavailable for plan
  if (isUnavailableForPlan.value) return false;
  return ['voice'].includes(key) && !isActive.value;
});

const onItemClick = () => {
  if (isActive.value && !isUnavailableForPlan.value) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :is-unavailable-for-plan="isUnavailableForPlan"
    :disabled="!isActive"
    @click="onItemClick"
  />
</template>
